import React, { useEffect, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Lock, CreditCard, ShieldCheck } from 'lucide-react';
import { SocialProofPopup } from './components/SocialProofPopup';
// --- Assets ---
const IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/btTEXcRKP8yCN7qXLfprVm/ChatGPT_Image_22_dic_2025,_00_50_53.png',
  bonus1: 'https://cdn.magicpatterns.com/uploads/s48TtxVF1Dx9ke1b6Mkg6y/ChatGPT_Image_22_dic_2025,_00_54_52.png',
  bonus2: 'https://cdn.magicpatterns.com/uploads/wWTr4XSXtFxJtihoVvNQ5Q/ChatGPT_Image_22_dic_2025,_00_58_44.png',
  bonus3: 'https://cdn.magicpatterns.com/uploads/f5B1qJnY4A2958G5ymKdeU/ChatGPT_Image_22_dic_2025,_01_01_39.png',
  bonus4: 'https://cdn.magicpatterns.com/uploads/cAzuQEtay7CAcCC5TJhLJv/ChatGPT_Image_22_dic_2025,_01_12_36.png'
};
const PURCHASE_LINK = 'https://checkout.ilumente.online/products/quincena-survival-reset';
// --- Components ---
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-16 md:py-24 px-4 md:px-8 max-w-5xl mx-auto ${className}`} data-id="element-0">
    {children}
  </section>;
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.8,
  delay,
  ease: 'easeOut'
}} data-id="element-1">
    {children}
  </motion.div>;
const Button = ({
  children,
  onClick,
  variant = 'primary',
  className = ''
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary';
  className?: string;
}) => {
  const baseStyles = 'inline-flex items-center justify-center px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg';
  const variants = {
    primary: 'bg-[#FF6B35] hover:bg-[#E85D2A] text-white shadow-[#FF6B35]/30',
    secondary: 'bg-[#0077B6] hover:bg-[#005F91] text-white shadow-[#0077B6]/30'
  };
  return <button onClick={onClick} className={`${baseStyles} ${variants[variant]} ${className}`} data-id="element-2">
      {children}
    </button>;
};
const TestimonialCard = ({
  name,
  city,
  role,
  text,
  image
}: {
  name: string;
  city: string;
  role: string;
  text: string;
  image: string;
}) => <motion.div whileHover={{
  y: -5
}} className="bg-white p-8 rounded-2xl shadow-xl border border-[#F5E6D3] relative overflow-hidden" data-id="element-3">
    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#FF6B35] to-[#F7931E]" data-id="element-4" />
    <div className="flex items-center gap-4 mb-6" data-id="element-5">
      <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden" data-id="element-6">
        <img src={image} alt={name} className="w-full h-full object-cover" data-id="element-7" />
      </div>
      <div data-id="element-8">
        <h4 className="font-bold text-gray-900 font-serif" data-id="element-9">{name}</h4>
        <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-10">
          {role} • {city}
        </p>
      </div>
    </div>
    <div className="flex gap-1 mb-4 text-[#FF6B35]" data-id="element-11">
      {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-12" />)}
    </div>
    <p className="text-gray-700 italic leading-relaxed font-serif" data-id="element-13">"{text}"</p>
  </motion.div>;
const BonusCard = ({
  number,
  title,
  description,
  image,
  value
}: {
  number: number;
  title: string;
  description: string;
  image: string;
  value: string;
}) => <motion.div initial={{
  opacity: 0,
  x: number % 2 === 0 ? 50 : -50
}} whileInView={{
  opacity: 1,
  x: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.8
}} className="flex flex-col md:flex-row gap-8 items-center bg-white rounded-3xl overflow-hidden shadow-xl border border-[#F5E6D3] mb-12" data-id="element-14">
    <div className="w-full md:w-2/5 h-64 md:h-auto relative group cursor-pointer" onClick={() => document.getElementById('purchase')?.scrollIntoView({
    behavior: 'smooth'
  })} data-id="element-15">
      <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-16" />
      <div className="absolute top-4 left-4 bg-[#FF6B35] text-white font-bold px-4 py-1 rounded-full shadow-lg" data-id="element-17">
        BONUS #{number}
      </div>
    </div>
    <div className="w-full md:w-3/5 p-8 md:pr-12" data-id="element-18">
      <h3 className="text-2xl md:text-3xl font-bold font-serif text-[#0077B6] mb-2" data-id="element-19">
        {title}
      </h3>
      <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-20">{description}</p>
      <div className="flex items-center justify-between border-t border-gray-100 pt-4" data-id="element-21">
        <span className="text-sm font-semibold text-gray-400 uppercase tracking-wider" data-id="element-22">
          Valor percibido
        </span>
        <span className="text-lg font-bold text-[#FF6B35] flex items-center gap-2" data-id="element-23">
          🔥 {value}
        </span>
      </div>
    </div>
  </motion.div>;
export function App() {
  const scrollToPurchase = () => {
    document.getElementById('purchase')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,800;1,400&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  return <div className="min-h-screen bg-[#FFF8F0] font-sans text-gray-800 overflow-x-hidden selection:bg-[#FF6B35] selection:text-white" data-id="element-24">
      <style data-id="element-25">{`
        :root {
          --font-serif: 'Playfair Display', serif;
          --font-sans: 'Inter', sans-serif;
        }
        h1, h2, h3, h4, .font-serif { font-family: var(--font-serif); }
        body { font-family: var(--font-sans); }
      `}</style>

      <SocialProofPopup data-id="element-26" />

      {/* --- HERO SECTION --- */}
      <header className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#003459] to-[#0077B6] text-white" data-id="element-27">
        <div className="absolute inset-0 opacity-40 mix-blend-overlay" data-id="element-28">
          <img src={IMAGES.main} alt="Background texture" className="w-full h-full object-cover blur-sm scale-110" data-id="element-29" />
        </div>

        <div className="container mx-auto px-4 relative z-10 grid md:grid-cols-2 gap-12 items-center pt-12 pb-20" data-id="element-30">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1
        }} className="text-left" data-id="element-31">
            <span className="inline-block px-4 py-1 rounded-full bg-[#FF6B35]/20 border border-[#FF6B35] text-[#FF6B35] font-semibold text-sm mb-6 tracking-wider backdrop-blur-sm" data-id="element-32">
              MÉTODO FINANCIERO DE EMERGENCIA
            </span>
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6 text-white drop-shadow-lg" data-id="element-33">
              QUINCENA <br data-id="element-34" />
              <span className="text-[#FFD166] italic" data-id="element-35">SURVIVAL RESET</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 font-light leading-relaxed max-w-lg" data-id="element-36">
              Cómo dejar de sobrevivir con la plata y recuperar control sin
              culpas, castigos ni fórmulas imposibles.
            </p>
            <Button onClick={scrollToPurchase} className="w-full md:w-auto" data-id="element-37">
              DEJAR DE SOBREVIVIR AHORA <ArrowRight className="ml-2 w-5 h-5" data-id="element-38" />
            </Button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          rotate: 2
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="relative cursor-pointer group" onClick={scrollToPurchase} data-id="element-39">
            <div className="absolute inset-0 bg-[#FF6B35] rounded-xl blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500" data-id="element-40"></div>
            <img src={IMAGES.main} alt="Quincena Survival Reset Book Cover" className="relative w-full max-w-md mx-auto rounded-xl shadow-2xl transform transition-transform duration-500 group-hover:-translate-y-2 group-hover:rotate-1" data-id="element-41" />
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-[#FFF8F0] to-transparent" data-id="element-42"></div>
      </header>

      {/* --- HOOK / OPENING --- */}
      <Section className="text-center max-w-3xl" data-id="element-43">
        <FadeIn data-id="element-44">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-8" data-id="element-45">
            Cobrás. Respirás. Por fin.
          </h2>
          <div className="prose prose-lg mx-auto text-gray-600 leading-relaxed" data-id="element-46">
            <p className="mb-4" data-id="element-47">
              Y a los pocos días… vuelve esa sensación conocida en el pecho.
            </p>
            <p className="mb-4" data-id="element-48">
              No sabés exactamente en qué se fue la plata. No hubo un gasto
              gigante. No hubo locuras.
            </p>
            <p className="mb-8 font-medium text-gray-800" data-id="element-49">
              Pero otra vez estás contando días. Otra vez evitás mirar el home
              banking. Otra vez te prometés: "el mes que viene me organizo
              mejor".
            </p>

            <div className="bg-[#F5E6D3]/30 p-8 rounded-2xl border-l-4 border-[#FF6B35] text-left my-10 shadow-sm" data-id="element-50">
              <p className="italic text-xl text-gray-700 mb-4" data-id="element-51">
                "Trabajo, me esfuerzo… ¿por qué sigo viviendo al día?"
              </p>
              <p className="text-gray-600" data-id="element-52">
                Y aunque no lo digas en voz alta, algo adentro tuyo duele.
              </p>
            </div>

            <p className="text-lg mb-2" data-id="element-53">
              Dejame decirte algo incómodo, pero profundamente liberador:
            </p>
            <div className="flex flex-wrap justify-center gap-4 font-bold text-[#0077B6] text-xl my-6" data-id="element-54">
              <span className="flex items-center gap-2" data-id="element-55">
                <Check className="w-5 h-5 text-[#FF6B35]" data-id="element-56" /> No sos
                irresponsable.
              </span>
              <span className="flex items-center gap-2" data-id="element-57">
                <Check className="w-5 h-5 text-[#FF6B35]" data-id="element-58" /> No sos incapaz.
              </span>
              <span className="flex items-center gap-2" data-id="element-59">
                <Check className="w-5 h-5 text-[#FF6B35]" data-id="element-60" /> No estás fallando.
              </span>
            </div>
            <p className="text-2xl font-serif font-bold text-gray-900 mt-8" data-id="element-61">
              Estás sobreviviendo.
            </p>
            <p className="text-gray-500" data-id="element-62">
              Y nadie te enseñó a manejar la plata desde ahí.
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* --- TARGET MARKET --- */}
      <section className="bg-white py-20 border-y border-[#F5E6D3]" data-id="element-63">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-64">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-65">
            <FadeIn data-id="element-66">
              <div className="relative" data-id="element-67">
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#FF6B35]/10 rounded-full blur-2xl" data-id="element-68"></div>
                <h2 className="text-4xl font-bold font-serif text-gray-900 mb-6 relative z-10" data-id="element-69">
                  ¿Para quién es <br data-id="element-70" />
                  <span className="text-[#FF6B35]" data-id="element-71">
                    QUINCENA SURVIVAL RESET?
                  </span>
                </h2>
                <p className="text-lg text-gray-600 mb-8" data-id="element-72">
                  No sos alguien que "se patina todo". No vivís de joda. No
                  gastás porque sí. Sos alguien que se esfuerza, pero igual vive
                  en modo alerta.
                </p>
                <Button onClick={scrollToPurchase} variant="secondary" className="text-sm px-6 py-3" data-id="element-73">
                  SI, SOY YO
                </Button>
              </div>
            </FadeIn>

            <div className="grid gap-4" data-id="element-74">
              {['Tenés entre 25 y 45 años', 'Trabajás, emprendés, hacés malabares', 'Sos freelancer, empleada/o, mamá, papá o todo junto', 'Tus ingresos son ajustados o inestables', 'Llegás a fin de mes… raspando', 'Vivís pendiente de la próxima quincena', 'Hacés lo que podés, pero igual no alcanza'].map((item, i) => <FadeIn delay={i * 0.1} key={i} data-id="element-75">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-[#FFF8F0] border border-[#F5E6D3] hover:border-[#FF6B35] transition-colors" data-id="element-76">
                    <div className="w-2 h-2 rounded-full bg-[#FF6B35]" data-id="element-77"></div>
                    <span className="text-gray-700 font-medium" data-id="element-78">{item}</span>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </div>
      </section>

      {/* --- PAIN POINTS --- */}
      <Section className="max-w-6xl" data-id="element-79">
        <div className="text-center mb-16" data-id="element-80">
          <span className="text-[#FF6B35] font-bold tracking-widest uppercase text-sm" data-id="element-81">
            El Problema Real
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mt-2" data-id="element-82">
            Por qué duele tanto
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-83">
          {[{
          title: 'La plata se va sin explicación',
          desc: 'Cobrás… y en días ya no sabés qué pasó. No fue un gasto grande. Fueron muchos pequeños. Y eso es lo peor: no lo ves venir.'
        }, {
          title: 'Vivir en ansiedad permanente',
          desc: 'Cada notificación del banco genera tensión. Cada gasto trae culpa. Cada imprevisto te desarma. Vivís reaccionando, nunca decidiendo.'
        }, {
          title: 'El autosabotaje financiero',
          desc: 'Sabés que algo no está funcionando. Te prometés cambiar. Arrancás motivada… y abandonás. No porque no quieras. Porque estás cansada.'
        }, {
          title: 'La vergüenza silenciosa',
          desc: 'No lo hablás con nadie. Pero sentís que "solo a vos te pasa". Te comparás. Te juzgás. Te callás.'
        }, {
          title: 'La sensación de estancamiento',
          desc: 'Trabajás. Te esforzás. Pero tu vida financiera no avanza. Y empieza el miedo más grande: "¿Y si mi vida va a ser siempre así?"'
        }].map((pain, i) => <FadeIn delay={i * 0.1} key={i} data-id="element-84">
              <div className={`h-full p-8 rounded-2xl bg-white shadow-lg border-t-4 border-[#0077B6] hover:shadow-xl transition-shadow ${i === 3 || i === 4 ? 'md:col-span-1 md:translate-x-1/2' : ''}`} data-id="element-85">
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-86">
                  {pain.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-87">{pain.desc}</p>
              </div>
            </FadeIn>)}
        </div>

        <div className="mt-20 bg-[#1A2E35] text-white p-10 md:p-16 rounded-3xl text-center relative overflow-hidden" data-id="element-88">
          <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-89"></div>
          <h3 className="text-2xl md:text-3xl font-serif font-bold mb-6 relative z-10" data-id="element-90">
            El Miedo de Fondo
          </h3>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-blue-100 mb-8 relative z-10" data-id="element-91">
            <span data-id="element-92">Pedir prestado otra vez</span> •
            <span data-id="element-93">Emergencias sin cubrir</span> •
            <span data-id="element-94">Sentirse incompetente</span> •
            <span data-id="element-95">Vivir al día por siempre</span>
          </div>
          <p className="text-xl font-light italic relative z-10" data-id="element-96">
            "Ese miedo no se va con frases lindas. Se va con claridad."
          </p>
        </div>
      </Section>

      {/* --- DREAM OUTCOME & SOLUTION --- */}
      <section className="py-20 bg-gradient-to-br from-[#FF6B35]/10 to-[#F5E6D3]/30" data-id="element-97">
        <Section data-id="element-98">
          <div className="grid md:grid-cols-2 gap-16" data-id="element-99">
            <FadeIn data-id="element-100">
              <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6" data-id="element-101">
                El Deseo Oculto
              </h2>
              <p className="text-lg text-gray-700 mb-6" data-id="element-102">
                En el fondo no querés ser millonaria. Querés algo mucho más
                simple:
              </p>
              <ul className="space-y-4" data-id="element-103">
                {['Cobrar y no entrar en pánico', 'Saber exactamente qué pasa con tu dinero', 'Tener un plan realista', 'Gastar sin culpa (porque decidís)', 'Abrir el banco sin miedo', 'Sentirte adulta, capaz, ordenada'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-800" data-id="element-104">
                    <div className="w-6 h-6 rounded-full bg-[#00B4D8] flex items-center justify-center text-white text-xs" data-id="element-105">
                      ✓
                    </div>
                    {item}
                  </li>)}
              </ul>
              <p className="mt-8 font-bold text-[#0077B6] text-xl" data-id="element-106">
                No querés abundancia falsa. Querés control real.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-107">
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-[#F5E6D3]" data-id="element-108">
                <h3 className="text-2xl font-bold text-[#FF6B35] mb-4" data-id="element-109">
                  La Verdad Incómoda
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-110">
                  No gastás mal. No sos desordenada. No te falta voluntad.
                </p>
                <p className="text-xl font-serif font-bold text-gray-900 mb-6" data-id="element-111">
                  👉 Nunca te enseñaron a manejar la plata cuando estás
                  sobreviviendo.
                </p>
                <p className="text-gray-600 mb-6" data-id="element-112">
                  Te dieron reglas pensadas para otra vida. Para otra calma.
                  Para otro margen. Y vos estás tratando de aplicar eso…
                  mientras te estás ahogando.
                </p>
                <div className="p-4 bg-[#FFF8F0] rounded-lg border border-[#F5E6D3]" data-id="element-113">
                  <p className="font-bold text-gray-800" data-id="element-114">
                    No es que vos no servís para el sistema.
                  </p>
                  <p className="text-[#FF6B35] font-bold" data-id="element-115">
                    👉 El sistema no sirve para tu realidad.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </Section>
      </section>

      {/* --- METAPHORS & SOLUTION REVEAL --- */}
      <Section className="text-center" data-id="element-116">
        <FadeIn data-id="element-117">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-12" data-id="element-118">
            La Pieza que Faltaba
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-16" data-id="element-119">
            QUINCENA SURVIVAL RESET no empieza por ahorrar. Empieza por dejar de
            sangrar. Porque no sirve traer más agua si el balde está agujereado.
          </p>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-8 mb-20" data-id="element-120">
          {[{
          icon: '🪣',
          title: 'El balde agujereado',
          text: 'No sirve ganar más si la plata se escapa sin que la veas.'
        }, {
          icon: '🚗',
          title: 'Manejar con el parabrisas tapado',
          text: 'No tomás malas decisiones: reaccionás porque no ves.'
        }, {
          icon: '🤿',
          title: 'Respirar bajo el agua',
          text: 'No es que no sepas nadar. Nadie te sacó a la superficie.'
        }].map((meta, i) => <FadeIn delay={i * 0.2} key={i} data-id="element-121">
              <div className="bg-white p-8 rounded-full aspect-square flex flex-col items-center justify-center shadow-lg hover:scale-105 transition-transform border-4 border-[#F5E6D3]" data-id="element-122">
                <span className="text-5xl mb-4" data-id="element-123">{meta.icon}</span>
                <h4 className="font-bold text-lg mb-2" data-id="element-124">{meta.title}</h4>
                <p className="text-sm text-gray-500" data-id="element-125">{meta.text}</p>
              </div>
            </FadeIn>)}
        </div>

        <div className="bg-[#0077B6] text-white p-12 rounded-3xl shadow-2xl transform md:rotate-1 max-w-4xl mx-auto" data-id="element-126">
          <h3 className="text-2xl md:text-4xl font-serif font-bold mb-6" data-id="element-127">
            LA GRAN PROMESA
          </h3>
          <p className="text-xl md:text-2xl leading-relaxed" data-id="element-128">
            En 30 días podés dejar de sobrevivir con la plata y sentir control
            real, aunque hoy estés al límite.
            <br data-id="element-129" />
            <span className="text-[#FFD166] font-bold mt-4 block" data-id="element-130">
              Sin castigarte. Sin dejar de vivir. Sin fórmulas imposibles.
            </span>
          </p>
        </div>
      </Section>

      {/* --- PRODUCT REVEAL --- */}
      <section className="py-20 bg-white" data-id="element-131">
        <div className="container mx-auto px-4" data-id="element-132">
          <div className="flex flex-col md:flex-row items-center gap-12 max-w-6xl mx-auto" data-id="element-133">
            <div className="w-full md:w-1/2" onClick={scrollToPurchase} data-id="element-134">
              <motion.img initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8
            }} src={IMAGES.main} alt="Product Cover" className="w-full rounded-lg shadow-2xl cursor-pointer hover:scale-105 transition-transform duration-500" data-id="element-135" />
            </div>
            <div className="w-full md:w-1/2" data-id="element-136">
              <FadeIn data-id="element-137">
                <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6" data-id="element-138">
                  ¿Qué es{' '}
                  <span className="text-[#FF6B35]" data-id="element-139">
                    QUINCENA SURVIVAL RESET?
                  </span>
                </h2>
                <p className="text-lg text-gray-600 mb-8" data-id="element-140">
                  Es un sistema práctico, humano y realista para:
                </p>
                <ul className="space-y-4 mb-8" data-id="element-141">
                  {['Entender tu dinero', 'Organizarlo sin rigidez', 'Salir del ciclo quincena–ansiedad–culpa', 'Recuperar control con lo que ya tenés'].map((item, i) => <li key={i} className="flex items-center gap-3 text-xl font-medium text-gray-800 border-b border-gray-100 pb-2" data-id="element-142">
                      <span className="text-[#FF6B35]" data-id="element-143">➜</span> {item}
                    </li>)}
                </ul>
                <p className="text-gray-500 italic mb-8" data-id="element-144">
                  No es teoría. No es motivación vacía. Es acción simple.
                </p>
                <Button onClick={scrollToPurchase} data-id="element-145">
                  QUIERO EL RESET AHORA
                </Button>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* --- BONUSES --- */}
      <section className="py-24 bg-[#FFF8F0] relative overflow-hidden" data-id="element-146">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-[radial-gradient(#FF6B35_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-147"></div>
        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-148">
          <div className="text-center mb-16" data-id="element-149">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4" data-id="element-150">
              Bonos Exclusivos
            </h2>
            <p className="text-xl text-gray-600" data-id="element-151">
              Herramientas diseñadas para acelerar tu paz mental
            </p>
          </div>

          <BonusCard number={1} title="Primeros 7 días SIN PÁNICO" description="Mini-guía de contención financiera inmediata. Una guía ultra simple para los primeros 7 días después de cobrar. Resuelve el momento más ansioso del mes y el desorden inicial que arruina todo." image={IMAGES.bonus1} value="Esto solo ya me salvó la quincena" data-id="element-152" />

          <BonusCard number={2} title="Planilla AntiAnsiedad (editable)" description="Ver para calmar. Una planilla extremadamente simple (Google Sheets o PDF editable). No es un presupuesto rígido, es un mapa visual calmante. Incluye semáforo emocional y espacio para gastos invisibles." image={IMAGES.bonus2} value="Herramienta práctica + sensación de orden inmediato" data-id="element-153" />

          <BonusCard number={3} title="Audio: 'Cobré y no entro en pánico'" description="Reset emocional financiero (10 minutos). Un audio guiado corto para escuchar apenas cobrás. Objetivo: Bajar la ansiedad antes de tomar decisiones. Incluye respiración, enfoque y reprogramación de culpa." image={IMAGES.bonus3} value="Muy alto (la gente paga por audios así)" data-id="element-154" />

          <BonusCard number={4} title="Lista Negra de Errores Invisibles" description="Lo que te hace perder plata sin darte cuenta. Checklist de errores comunes que NO parecen errores. Ejemplos: 'Después veo', 'Es poquito', 'Me lo merezco'. Elimina culpa y te da claridad." image={IMAGES.bonus4} value="Claridad = Poder" data-id="element-155" />
        </div>
      </section>

      {/* --- TESTIMONIALS --- */}
      <section className="py-24 bg-white border-t border-[#F5E6D3]" data-id="element-156">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-157">
          <h2 className="text-4xl font-serif font-bold text-center text-gray-900 mb-16" data-id="element-158">
            Historias de quienes ya hicieron el Reset
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-159">
            <div className="space-y-8" data-id="element-160">
              <TestimonialCard name="Camila R." city="Buenos Aires" role="Freelancer, 29 años" text="Siempre pensé que ganaba poco, pero el problema era el desorden. Con el audio de 'Cobré y no entro en pánico' pude frenar la ansiedad de gastar todo el primer día. Por primera vez llegué al día 20 con plata en la cuenta." image="https://randomuser.me/api/portraits/women/44.jpg" data-id="element-161" />
              <TestimonialCard name="Javier M." city="Santiago" role="Empleado Administrativo, 34 años" text="La planilla AntiAnsiedad es lo mejor que me pasó. Simple, visual, sin cosas raras. Dejé de tenerle miedo al home banking. No me sobró plata el primer mes, pero dormí tranquilo. Eso no tiene precio." image="https://randomuser.me/api/portraits/men/32.jpg" data-id="element-162" />
            </div>
            <div className="space-y-8 md:mt-12" data-id="element-163">
              <TestimonialCard name="Valentina S." city="Bogotá" role="Mamá y Emprendedora, 31 años" text="Estaba cansada de sentirme culpable cada vez que compraba algo para mi hijo. Este método me enseñó a diferenciar supervivencia de vida. El concepto de 'dejar de sangrar' me cambió la cabeza. Gracias infinitas." image="https://randomuser.me/api/portraits/women/68.jpg" data-id="element-164" />
              <TestimonialCard name="Diego F." city="Ciudad de México" role="Ventas, 42 años" text="A mis 40 sentía vergüenza de vivir al día. Pensé que ya era tarde para aprender. El Reset es brutalmente honesto pero no te juzga. Me sentí acompañado y entendido. Recuperé el control en serio." image="https://randomuser.me/api/portraits/men/86.jpg" data-id="element-165" />
            </div>
            <div className="space-y-8" data-id="element-166">
              <TestimonialCard name="Sofía L." city="Lima" role="Docente, 27 años" text="Probé mil apps y excels complicados. Los abandonaba a la semana. Esto es distinto porque ataca la emoción, no solo el número. La lista negra de errores invisibles me abrió los ojos. Recomendadísimo." image="https://randomuser.me/api/portraits/women/22.jpg" data-id="element-167" />
            </div>
          </div>
        </div>
      </section>

      {/* --- PRICING / PURCHASE --- */}
      <section id="purchase" className="py-24 bg-[#003459] text-white relative overflow-hidden" data-id="element-168">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-169"></div>
        <div className="container mx-auto px-4 max-w-4xl relative z-10 text-center" data-id="element-170">
          <FadeIn data-id="element-171">
            <h2 className="text-4xl md:text-6xl font-serif font-bold mb-8" data-id="element-172">
              Tu Reset Comienza Hoy
            </h2>
            <p className="text-xl md:text-2xl text-blue-200 mb-12 max-w-2xl mx-auto" data-id="element-173">
              No necesitás otra promesa vacía. Necesitás claridad. <br data-id="element-174" />
              No necesitás castigarte más. Necesitás un reset.
            </p>

            <div className="bg-white text-gray-900 rounded-3xl p-8 md:p-12 shadow-2xl max-w-2xl mx-auto transform hover:scale-[1.02] transition-transform duration-300" data-id="element-175">
              <div className="flex justify-center mb-6" data-id="element-176">
                <img src={IMAGES.main} alt="Pack Completo" className="w-48 rounded shadow-lg" data-id="element-177" />
              </div>
              <h3 className="text-2xl font-bold mb-2" data-id="element-178">
                QUINCENA SURVIVAL RESET
              </h3>
              <p className="text-gray-500 mb-6" data-id="element-179">+ Todos los bonos incluidos</p>

              <div className="flex items-baseline justify-center gap-2 mb-8" data-id="element-180">
                <span className="text-5xl md:text-7xl font-bold text-[#FF6B35]" data-id="element-181">
                  $19.990
                </span>
                <span className="text-gray-400 font-medium" data-id="element-182">ARS</span>
              </div>

              <a href={PURCHASE_LINK} target="_blank" rel="noopener noreferrer" className="block w-full bg-[#FF6B35] hover:bg-[#E85D2A] text-white font-bold text-xl py-5 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mb-4" data-id="element-183">
                DESCARGAR AHORA
              </a>

              <div className="flex items-center justify-center gap-4 text-sm text-gray-500 mt-6" data-id="element-184">
                <span className="flex items-center gap-1" data-id="element-185">
                  <Lock size={14} data-id="element-186" /> Pago Seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-187">
                  <CreditCard size={14} data-id="element-188" /> Acceso Inmediato
                </span>
                <span className="flex items-center gap-1" data-id="element-189">
                  <ShieldCheck size={14} data-id="element-190" /> Garantía de Calidad
                </span>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap justify-center gap-6 text-blue-200 font-medium" data-id="element-191">
              <span data-id="element-192">👉 Hacé el reset hoy.</span>
              <span data-id="element-193">👉 Dejá de sobrevivir.</span>
              <span data-id="element-194">👉 Recuperá el control.</span>
              <span data-id="element-195">👉 Empezá con lo que tenés.</span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="bg-[#001F35] text-gray-400 py-12 text-center text-sm" data-id="element-196">
        <div className="container mx-auto px-4" data-id="element-197">
          <p className="mb-4" data-id="element-198">
            © {new Date().getFullYear()} Quincena Survival Reset. Todos los
            derechos reservados.
          </p>
          <p data-id="element-199">
            Este producto no garantiza resultados económicos sin acción. Es una
            guía educativa basada en experiencias reales.
          </p>
        </div>
      </footer>
    </div>;
}